import { useMutation } from '@tanstack/react-query';
import { Button, cx, Icon, Notice } from '@raro/ui';
import { useEffect, useRef, useState } from 'react';
import { useAiStatus } from '../lib/ai';
import { api } from '../lib/api';

type Topic = 'conversation_goal';
type Msg = { role: 'user' | 'assistant'; text: string };
type Review = { reply: string; verdict: 'ready' | 'needs_work'; proposal: string | null; model: string };
type Speech = { start: () => void; stop: () => void; onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null; onend: (() => void) | null; onerror: (() => void) | null; lang: string; interimResults: boolean; continuous: boolean };
const SpeechCtor = (): (new () => Speech) | null => ((window as unknown as { SpeechRecognition?: new () => Speech }).SpeechRecognition ?? (window as unknown as { webkitSpeechRecognition?: new () => Speech }).webkitSpeechRecognition ?? null);

/**
 * Chat de parecer: a Machine lê um texto, diz se ele chega ao objetivo e propõe uma versão.
 * A pessoa conversa (texto ou voz) até concordar e clicar em "definir", que devolve a proposta ao formulário.
 * Reutilizável: `topic` diz para que o texto serve (lista em `REVIEW_TOPICS`, no pacote de IA).
 */
export function MachineChat({ topic, draft, organizationId, projectId, onAccept, onClose, title = 'parecer da Machine' }: { topic: Topic; draft: string; organizationId?: string | null; projectId?: string | null; onAccept: (text: string) => void; onClose: () => void; title?: string }) {
  const status = useAiStatus();
  const llm = Boolean(status.data?.llm);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [proposal, setProposal] = useState<string | null>(null);
  const [verdict, setVerdict] = useState<'ready' | 'needs_work' | null>(null);
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const recRef = useRef<Speech | null>(null);
  const endRef = useRef<HTMLDivElement | null>(null);
  const ask = useMutation({
    mutationFn: (history: Msg[]) => api.post<Review>('/ai/review', { topic, draft: proposal ?? draft, messages: history, organizationId: organizationId ?? undefined, projectId: projectId ?? undefined }),
    onSuccess: (r, history) => { setMessages([...history, { role: 'assistant', text: r.reply }]); setVerdict(r.verdict); if (r.proposal) setProposal(r.proposal); else if (r.verdict === 'ready' && !proposal) setProposal(draft); },
  });
  useEffect(() => { if (llm && messages.length === 0 && !ask.isPending) ask.mutate([]); }, [llm]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [messages, ask.isPending]);
  const send = () => { const t = text.trim(); if (!t || ask.isPending) return; recRef.current?.stop(); const history = [...messages, { role: 'user' as const, text: t }]; setMessages(history); setText(''); ask.mutate(history); };
  const canListen = Boolean(SpeechCtor());
  const listen = () => {
    const Ctor = SpeechCtor(); if (!Ctor) return;
    const rec = new Ctor(); rec.lang = 'pt-BR'; rec.interimResults = true; rec.continuous = true;
    let final = text ? `${text} ` : '';
    rec.onresult = (e) => { let interim = ''; for (let i = 0; i < e.results.length; i++) { const r = e.results[i]!; if (r.isFinal) final += `${r[0]!.transcript} `; else interim += r[0]!.transcript; } setText((final + interim).trim()); };
    rec.onend = () => { setListening(false); recRef.current = null; };
    rec.onerror = () => { setListening(false); recRef.current = null; };
    recRef.current = rec; setListening(true); rec.start();
  };
  return (
    <div className="rr-chat">
      <div className="rr-row rr-row--between">
        <span className="rr-label">{title}</span>
        <button type="button" className="rr-budget__toggle" style={{ fontSize: 'var(--rr-text-xs)' }} onClick={onClose}>fechar</button>
      </div>
      {status.data && !llm && <Notice tone="error">sem modelo configurado: a Machine não consegue dar parecer.</Notice>}
      <div className="rr-chat__turns">
        {messages.map((m, i) => (
          <div key={i} className={cx('rr-turn', `rr-turn--${m.role}`)}>
            {m.role === 'assistant' && <span className="rr-turn__who rr-turn__who--machine">M</span>}
            <div className="rr-turn__bubble rr-chat__bubble">{m.text}</div>
          </div>
        ))}
        {ask.isPending && <div className="rr-turn rr-turn--assistant"><span className="rr-turn__who rr-turn__who--machine">M</span><div className="rr-turn__bubble rr-turn__bubble--thinking">···</div></div>}
        {ask.isError && <Notice tone="error">{(ask.error as Error).message}</Notice>}
        <div ref={endRef} />
      </div>
      {proposal && (
        <div className={cx('rr-chat__proposal', verdict === 'ready' && 'rr-chat__proposal--ready')}>
          <div className="rr-row rr-row--between"><span className="rr-label">{verdict === 'ready' ? 'pronto para definir' : 'proposta da Machine'}</span></div>
          <pre className="rr-chat__proposal-text">{proposal}</pre>
          <div className="rr-row" style={{ gap: 6 }}>
            <Button size="sm" variant="primary" onClick={() => onAccept(proposal)}>definir</Button>
            <span className="rr-faint" style={{ fontSize: 'var(--rr-text-xs)' }}>ou continue conversando até concordar.</span>
          </div>
        </div>
      )}
      <div className="rr-chat__composer">
        <textarea className="rr-textarea" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={listening ? 'ouvindo…' : 'responda à Machine: discorde, peça ajustes, explique o que falta'} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <div className="rr-row rr-row--between">
          {canListen ? <button type="button" className={cx('rr-mic', listening && 'rr-mic--on')} onClick={() => (listening ? recRef.current?.stop() : listen())}><Icon name={listening ? 'stop' : 'mic'} size={14} /> {listening ? 'parar' : 'falar'}</button> : <span />}
          <Button size="sm" variant="primary" disabled={!llm || !text.trim() || ask.isPending} onClick={send}>enviar</Button>
        </div>
      </div>
    </div>
  );
}
